import React from 'react';
import WikiIcon from '../shared/WikiIcon';
import { EDITOR_COPY, TOOLBAR_GROUPS } from './editorCopy';

const FMT_LETTER = { bold: 'b', italic: 'i', strike: 's' };

/** Whether a toolbar button reads as active for the caret key "blockKind|fmt" (prototype `tb(on)`). */
export function isToolActive(kind, toolbarKey) {
    const [blk = 'p', fmt = ''] = String(toolbarKey || 'p|').split('|');
    if (FMT_LETTER[kind]) return fmt.indexOf(FMT_LETTER[kind]) > -1 && !/^h/.test(blk);
    if (kind === 'code' || kind === 'link' || kind === 'table' || kind === 'image' || kind === 'video' || kind === 'hr') return false;
    return blk === kind;
}

/**
 * Row 2 of the sticky header: the Write | Markdown switch, then (Write) the
 * 20 formatting buttons in four groups, or (Markdown) the stored-Markdown
 * note. Buttons act on mousedown + preventDefault so the surface keeps focus
 * and its selection (prototype); keyboard activation (Enter/Space, detail 0)
 * runs the same command.
 */
export default function WikiEditorToolbar({ mode, toolbarKey, onMode, onCommand }) {
    const press = (kind) => ({
        onMouseDown: (event) => {
            event.preventDefault();
            onCommand(kind);
        },
        onClick: (event) => {
            if (event.detail === 0) onCommand(kind);
        },
    });
    return (
        <div className="wk-ed-row2" data-testid="wiki-editor-toolbar">
            <div className="wk-ed-modes" role="tablist" aria-label="Editor mode">
                {[['write', EDITOR_COPY.modeWrite, 'lucide:pencil'], ['md', EDITOR_COPY.modeMarkdown, 'lucide:hash']].map(([m, label, icon]) => (
                    <button
                        key={m}
                        type="button"
                        role="tab"
                        aria-selected={mode === m}
                        className={`wk-ed-mode${mode === m ? ' is-active' : ''}`}
                        data-testid={`wiki-editor-mode-${m}`}
                        onClick={() => onMode(m)}
                    >
                        <WikiIcon name={icon} size={13} />
                        {label}
                    </button>
                ))}
            </div>
            {mode === 'write' ? TOOLBAR_GROUPS.map((group, gi) => (
                <React.Fragment key={group[0].kind}>
                    {gi > 0 && <span className="wk-ed-sep" aria-hidden="true" />}
                    {group.map((tool) => {
                        const active = isToolActive(tool.kind, toolbarKey);
                        return (
                            <button
                                key={tool.kind}
                                type="button"
                                title={tool.title}
                                aria-label={tool.title}
                                aria-pressed={active}
                                className={`wk-ed-tb${active ? ' is-active' : ''}${tool.variant ? ` wk-ed-tb--${tool.variant}` : ''}`}
                                data-testid={`wiki-editor-tb-${tool.kind}`}
                                {...press(tool.kind)}
                            >
                                {tool.icon ? <WikiIcon name={tool.icon} size={15} /> : tool.label}
                            </button>
                        );
                    })}
                </React.Fragment>
            )) : (
                <span className="wk-ed-md-note">{EDITOR_COPY.markdownToolbarNote}</span>
            )}
        </div>
    );
}
